import { useMemo, useState } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { getTrans, getTransWithParams } from 'modules/i18n';
import { textFor } from '../localizedText';
import {
  signalCountPill,
  signalNote,
  signalOptionDescription,
  signalOptionLabel,
  signalPagingArrow,
  signalTray,
  signalTraySegment
} from '../signalStyles';
import { BlockHeading } from './BlockHeading';
import {
  chooseLevel,
  ItemScaleItem,
  ratedCount,
  readItemScaleAnswer,
  readItemScaleConfig
} from './itemScale';
import { BlockRendererProps, questionIdOf } from './types';

/**
 * `item_scale@1`: several things rated on one shared scale.
 *
 * ONE SCALE FOR EVERY ITEM is the whole point of the type — it is what makes the answers comparable —
 * so the levels are rendered identically under each item rather than as a matrix with a header row. A
 * matrix reads well on a desktop and collapses on a phone, where the header scrolls out of sight and a
 * respondent is left choosing between unlabelled columns.
 *
 * SELF-ASSESSMENT, NOT A QUALIFICATION. Nothing downstream may read a level as verified, and there is
 * deliberately no total across the items: adding up self-assessments produces a number that looks like a
 * measurement and is not one. The note at the foot says so to the respondent as well, because someone
 * rating themselves honestly deserves to know it is not a test.
 *
 * TWO MODES, and the difference is what finishing means. `ROWS` shows everything at once and lets a
 * respondent answer in any order. `DECK` shows one item at a time in a fixed order and requires every
 * card, which is why its progress is announced — a fixed order with no sense of how much is left is the
 * interaction people abandon.
 */
export const ItemScaleBlock = ({
  block,
  answer,
  onChange,
  locale
}: BlockRendererProps) => {
  const config = useMemo(() => readItemScaleConfig(block), [block]);
  const selections = readItemScaleAnswer(answer)?.selections ?? {};

  const questionId = questionIdOf(block);

  const rated = ratedCount(selections, config);

  /*
    The deck's position, held here rather than in the answer.

    It is a reading position, not something anyone said — storing it would put UI state into a
    respondent's submitted data, and resuming a half-finished form would then restore a card index
    rather than recompute it. The card shown is the first unrated one, so a resumed form lands where the
    work is.
  */
  const firstUnrated = config.items.findIndex(item => !selections[item.key]);
  const [cursor, setCursor] = useState(() =>
    firstUnrated === -1 ? 0 : firstUnrated
  );
  const isDeck = config.mode === 'DECK';
  const visible = isDeck
    ? config.items.slice(cursor, cursor + 1)
    : config.items;

  const choose = (itemKey: string, levelKey: string) => {
    onChange({ selections: chooseLevel(selections, itemKey, levelKey) });
    // Advance only forwards, and only off a card that now has an answer — never past the end, and
    // never on the tap that CLEARS a level, which would look like the card ran away.
    if (isDeck && selections[itemKey] !== levelKey) {
      setCursor(current => Math.min(current + 1, config.items.length - 1));
    }
  };

  return (
    <Stack spacing={3}>
      {/* The hint says the scale is answered once per item, which a single row of levels does not
          say on its own — ALONGSIDE the operator's help, never instead of it. */}
      <BlockHeading
        block={block}
        locale={locale}
        hint={getTrans('guestForm.itemScale.hint')}
      />

      <Stack spacing={2.5} role="group" aria-labelledby={questionId}>
        {visible.map(item => (
          <ItemRow
            key={item.key}
            item={item}
            levels={config.levels}
            chosen={selections[item.key]}
            showDescription={config.showItemDescriptions}
            locale={locale}
            onChoose={levelKey => choose(item.key, levelKey)}
          />
        ))}
      </Stack>

      {/*
        Paging, and it is not a convenience.

        The deck advances itself on every answer, so without a way back a mis-tap is unrecoverable:
        the card is gone, the wrong level is stored, and the only remaining route is abandoning the
        form. Forward is offered too, because a respondent who paged back to check something should
        not have to re-answer a card to get where they were.
      */}
      {isDeck && config.items.length > 1 && (
        <Stack direction="row" spacing={0.75} justifyContent="flex-end">
          <ButtonBase
            onClick={() => setCursor(current => Math.max(0, current - 1))}
            disabled={cursor === 0}
            aria-label={getTrans('guestForm.itemScale.previousCard')}
            data-testid="form-item-scale-prev"
            sx={signalPagingArrow}
          >
            <ChevronLeftIcon sx={{ fontSize: 15 }} aria-hidden="true" />
          </ButtonBase>
          <ButtonBase
            onClick={() =>
              setCursor(current =>
                Math.min(config.items.length - 1, current + 1)
              )
            }
            disabled={cursor >= config.items.length - 1}
            aria-label={getTrans('guestForm.itemScale.nextCard')}
            data-testid="form-item-scale-next"
            sx={signalPagingArrow}
          >
            <ChevronRightIcon sx={{ fontSize: 15 }} aria-hidden="true" />
          </ButtonBase>
        </Stack>
      )}

      {/* Announced as it changes — for a deck it is the only sense of how much is left, and for rows
          it is the acknowledgement a tap otherwise never gets. */}
      <Stack spacing={1} role="status" aria-live="polite">
        <Box
          component="span"
          sx={signalCountPill(rated === config.items.length)}
          data-testid="form-item-scale-progress"
        >
          {getTransWithParams('guestForm.itemScale.progress', {
            done: rated,
            total: config.items.length
          })}
        </Box>
      </Stack>

      <Box sx={signalNote('neutral')}>
        {getTrans('guestForm.itemScale.selfAssessment')}
      </Box>
    </Stack>
  );
};

/**
 * One item and its scale.
 *
 * The label is a `legend` inside a `fieldset`, which is what makes a screen reader announce "Forklift"
 * before each level rather than reading five bare words with no subject. `radio` roles rather than
 * buttons, because exactly one level applies at a time and that is what the role means.
 */
const ItemRow = ({
  item,
  levels,
  chosen,
  showDescription,
  locale,
  onChoose
}: {
  item: ItemScaleItem;
  levels: { key: string; label: LocalizedTextLike }[];
  chosen: string | undefined;
  showDescription: boolean;
  locale: string;
  onChoose: (levelKey: string) => void;
}) => {
  const description = showDescription
    ? textFor(item.description, locale)
    : null;

  return (
    <Box component="fieldset" sx={{ border: 0, p: 0, m: 0 }}>
      {/* The same weight and size an option's label carries elsewhere in the flow: an item IS the
          thing being answered here, and it should not read as smaller than a choice does. */}
      <Typography component="legend" sx={signalOptionLabel}>
        {textFor(item.label, locale)}
      </Typography>
      {description && (
        <Typography sx={signalOptionDescription}>{description}</Typography>
      )}

      {/* A tray, because these are short comparable options and a tray says "here is a range" where
          stacked rows would say "here is a list". */}
      <Box sx={{ ...signalTray, mt: 1.25 }} role="radiogroup">
        {levels.map(level => {
          const isSelected = chosen === level.key;
          return (
            <ButtonBase
              key={level.key}
              role="radio"
              aria-checked={isSelected}
              onClick={() => onChoose(level.key)}
              data-testid={`form-item-${item.key}-${level.key}`}
              sx={signalTraySegment(isSelected)}
            >
              <Typography sx={{ fontSize: 13, lineHeight: 1.25 }}>
                {textFor(level.label, locale)}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
};

/** Local alias so the row does not have to import the api-types shape for one prop. */
type LocalizedTextLike = Parameters<typeof textFor>[0];
